import { APP_NAME, CUBIC_BOT_GITHUB_USER_IDS, CUBIC_BOT_NAMES } from '@/lib/constants';
import { isDevelopment } from '@/lib/env';

const LEGACY_EXTERNAL_REVIEW_BOT_LOGINS = ['coderabbitai', 'qodo-code-review', 'copilot'] as const;

const NON_REVIEW_AUTOMATION_LOGINS = [
  'dependabot[bot]',
  'renovate[bot]',
  'codecov[bot]',
  'codecov-commenter[bot]',
  'netlify[bot]',
  'vercel[bot]',
  'github-actions[bot]',
  'stale[bot]',
  'mergify[bot]',
  'greenkeeper[bot]',
  'depfu[bot]',
  'imgbot[bot]',
  'allcontributors[bot]',
  'release-please[bot]',
  'changeset-bot[bot]',
  'semantic-release-bot',
  'lgtm-com[bot]',
  'size-limit-action[bot]',
] as const;

/**
 * Simple check if a GitHub login refers to a bot account.
 * GitHub conventionally appends `[bot]` to all bot account logins.
 */
export function isBotLogin(login: string): boolean {
  return login.toLowerCase().endsWith('[bot]');
}

/**
 * Checks whether a GitHub account is one of Cubic's own bot accounts.
 *
 * Use the immutable GitHub user id for billing/review gates.
 */
export function isCubicBotAccount(githubUserId: number | string): boolean {
  return CUBIC_BOT_GITHUB_USER_IDS.includes(githubUserId.toString());
}

/**
 * Checks whether a GitHub login is one of Cubic's own bot logins
 * (exact match against CUBIC_BOT_NAMES, case-insensitive).
 *
 * Prefer `isCubicBotAccount` with the immutable user id where an id is
 * available.
 */
export function isCubicBotLogin(login?: string | null): boolean {
  const normalizedLogin = login?.trim().toLowerCase();
  if (!normalizedLogin) {
    return false;
  }

  return CUBIC_BOT_NAMES.some((botLogin) => botLogin.toLowerCase() === normalizedLogin);
}

/**
 * Check if a user login belongs to a MRGE/Cubic bot account, accepting both
 * the `[bot]` and bare app-name forms. Matches exactly: substring matching
 * would also accept lookalike logins (e.g. `fake-cubic-dev-ai[bot]`).
 */
export function isMrgeBot(login?: string | null): boolean {
  const normalizedLogin = login?.trim().toLowerCase();
  if (!normalizedLogin) {
    return false;
  }

  return (
    isCubicBotLogin(normalizedLogin) ||
    normalizedLogin === APP_NAME.PROD ||
    normalizedLogin === APP_NAME.DEV
  );
}

/**
 * Get the list of MRGE/Cubic bot login names (for filtering, matching, etc.)
 */
export function getMrgeBotLogins(): string[] {
  // In production, only return prod bot
  // In development, return both prod and dev bots
  if (isDevelopment()) {
    return [`${APP_NAME.PROD}[bot]`, `${APP_NAME.DEV}[bot]`];
  }
  return [`${APP_NAME.PROD}[bot]`];
}

export function isNonReviewAutomationLogin(login?: string | null): boolean {
  if (!login) {
    return false;
  }

  return NON_REVIEW_AUTOMATION_LOGINS.includes(
    login.toLowerCase() as (typeof NON_REVIEW_AUTOMATION_LOGINS)[number],
  );
}

export function isLegacyExternalReviewBotLogin(login?: string | null): boolean {
  if (!login) {
    return false;
  }

  return LEGACY_EXTERNAL_REVIEW_BOT_LOGINS.includes(
    login.toLowerCase() as (typeof LEGACY_EXTERNAL_REVIEW_BOT_LOGINS)[number],
  );
}

export function getLegacyExternalReviewBotLogins(): string[] {
  return [...LEGACY_EXTERNAL_REVIEW_BOT_LOGINS];
}

/**
 * Whether an account belongs to automation rather than a person: any account
 * GitHub types as `Bot`, GitHub App bots (`[bot]` suffix), and known bot accounts
 * that post as regular users, such as older review bots and release tooling.
 * Pass GitHub's `user.type` when available because it is authoritative. Cubic's
 * own bots match too; callers that trust them check `isCubicBotLogin` first.
 */
export function isAutomationLogin(login?: string | null, userType?: string | null): boolean {
  if (userType?.toLowerCase() === 'bot') {
    return true;
  }
  if (!login) {
    return false;
  }

  return (
    isBotLogin(login) || isLegacyExternalReviewBotLogin(login) || isNonReviewAutomationLogin(login)
  );
}

export function isExternalReviewBot(params: {
  login?: string | null;
  userType?: string | null;
}): boolean {
  if (!params.login) {
    return false;
  }

  if (isMrgeBot(params.login) || isNonReviewAutomationLogin(params.login)) {
    return false;
  }

  if (params.userType?.toLowerCase() === 'bot' || isBotLogin(params.login)) {
    return true;
  }

  return isLegacyExternalReviewBotLogin(params.login);
}

/**
 * Detects PR authors that need bot-specific review billing gates.
 *
 * Unlike `isExternalReviewBot`, this includes non-review automation bots because
 * bot-authored PRs should not consume free public reviews unless explicitly seated.
 */
export function isBotPrAuthorForReviewGate(params: {
  login?: string | null;
  userType?: string | null;
}): boolean {
  return (
    params.userType?.toLowerCase() === 'bot' ||
    isBotLogin(params.login ?? '') ||
    isLegacyExternalReviewBotLogin(params.login)
  );
}
